import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import includes from 'lodash/includes';
import { yieldCallback } from '@zillow/yield-callback';

// Actions
import AppActions from 'app/shared/flux/actions/AppActions';

// Components
import CreateSearchAlertModal from 'app/shared/modules/user-search/CreateSearchAlertModal';
import MapWrapper from 'app/shared/wrappers/MapWrapper';
import NotFoundPage from 'app/shared/pages/NotFoundPage';
import OptInSearchAlertsModal from 'app/shared/modules/OptInSearchAlertsModal';
import SplitMapSidebar from 'app/shared/modules/SplitMapSidebar';
import SlidingPreviewContainer from 'app/shared/modules/map/SlidingPreviewContainer';

// Hooks
import { useAreaResolution, useListingsFetchOrchestrator, useMapViewToggle } from './hooks';

// Misc / Utils
import 'app/shared/templates/SplitMapTemplate/style.scss';
import { shouldShowPreview } from 'app/shared/templates/SplitMapTemplate/controller';
import { SIDEBAR_ELEMENT_ID } from './constants';
import queryUtils from 'app/shared/utils/queryUtils';
import routeUtils from 'app/shared/utils/routeUtils';

// Types
import type { RootReduxState } from 'app/shared/flux/reducers';
import type { AppThunkDispatch } from 'app/shared/flux/store/configureStore';
import type { SplitMapTemplateClassProps } from './types';

// ─── Class Component (shrunk — only rendering + redirect + modal) ───────────

class SplitMapTemplateClass extends React.Component<SplitMapTemplateClassProps> {
  componentDidMount() {
    const { isAreaUrl, pathname } = this.props;
    const isFsSRP = includes(pathname, 'for-sale') && isAreaUrl;

    if (isFsSRP) {
      const pathparts = pathname.split('/');
      pathparts[pathparts.length - 1] = 'apartments-for-rent';
      window.router.transitionTo(pathparts.join('/'));
    }
  }

  handleClosingModalOverlay = yieldCallback((modalName: string) => {
    const { dispatch } = this.props;
    dispatch(AppActions.toggleOverlay(modalName, false));
  });

  render() {
    const {
      currentListing,
      fetchListings,
      isAreaUrl,
      isMobile,
      isNearMeUrl,
      isPadOrBuildingUrl,
      isValidAreaPage,
      isValidSearchSlug,
      pathname,
      previewListing,
      setMapView,
      showCreateSearchAlert,
      showMap,
      showOptInSearchAlerts,
    } = this.props;

    const showPreview = shouldShowPreview({
      currentListing: currentListing ?? undefined,
      pathname,
      hideMobileRightSidebar: showMap,
      previewListing: previewListing ?? undefined,
    });

    const shouldShowNotFoundPage = !isValidAreaPage || !isValidSearchSlug;

    if (shouldShowNotFoundPage) {
      return <NotFoundPage />;
    }

    const sidebarProps = {
      isMapView: showMap,
      setMapView,
      sidebarId: SIDEBAR_ELEMENT_ID,
      fetchListings,
    };

    const isAreaPage = isAreaUrl || isNearMeUrl || (isPadOrBuildingUrl && !isMobile);

    return (
      <>
        <div className="SplitMapTemplate">
          {showMap && <MapWrapper isMapView={showMap} useStaticMap={isAreaPage} />}
          {showPreview && previewListing && <SlidingPreviewContainer listing={previewListing} />}
          <SplitMapSidebar key={pathname} {...this.props} {...sidebarProps} />
          {showCreateSearchAlert && (
            <CreateSearchAlertModal
              onHidePopup={() => this.handleClosingModalOverlay('createSearchAlert')}
              triggerLocation="search_results"
              triggerObject="search_results|alerts_modal"
            />
          )}
          {showOptInSearchAlerts && (
            <OptInSearchAlertsModal onHidePopup={() => this.handleClosingModalOverlay('optInSearchAlerts')} />
          )}
        </div>
      </>
    );
  }
}

// ─── Functional Wrapper ─────────────────────────────────────────────────────

const SplitMapTemplate = () => {
  const dispatch = useDispatch() as AppThunkDispatch;
  const location = useLocation();
  const parsedQuery = queryUtils.parse(location.search);

  // Redux selectors
  const area = useSelector((state: RootReduxState) => state.area.area);
  const byCoordsListings = useSelector((state: RootReduxState) => state.listings.listingGroups.byCoords);
  const byCoordsListingCount = useSelector((state: RootReduxState) => state.listings.listingGroups.byCoords.length);
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails.currentListing);
  const existsOutsideOfList = useSelector((state: RootReduxState) => state.currentListingDetails.existsOutsideOfList);
  const gmapLoaded = useSelector((state: RootReduxState) => state.app.gmapLoaded);
  const isInitialSsrPage = useSelector((state: RootReduxState) => state.app.isInitialSsrPage);
  const isLoggedIn = useSelector((state: RootReduxState) => state.user.loggedIn);
  const isMobile = useSelector((state: RootReduxState) => state.app.device.screenWidth === 'sm');
  const isValidAreaPage = useSelector((state: RootReduxState) => state.app.isValidAreaPage);
  const isValidSearchSlug = useSelector((state: RootReduxState) => state.app.isValidSearchSlug);
  const previewListing = useSelector((state: RootReduxState) => state.listings.listingGroups.previewListing);
  const showCreateSearchAlert = useSelector((state: RootReduxState) => state.app.overlays.createSearchAlert);
  const showOptInSearchAlerts = useSelector((state: RootReduxState) => state.app.overlays.optInSearchAlerts);
  const slug = useSelector((state: RootReduxState) => state.filter.search.slug);

  // Derived route checks
  const pathname = location.pathname;
  const isAreaUrl = routeUtils.isAreaUrl(pathname);
  const isNearMeUrl = routeUtils.isNearMeUrl(pathname);
  const isPadOrBuildingUrl = routeUtils.isPadOrBuildingUrl(pathname);

  // Hooks
  const { isMapView, setMapView, showMap } = useMapViewToggle({ isMobile });

  const { resolveListingArea } = useAreaResolution({
    area,
    currentListing,
    dispatch,
    isMapView,
    pathname,
    query: parsedQuery,
  });

  const { fetchListings } = useListingsFetchOrchestrator({
    area,
    border: parsedQuery.border,
    byCoordsListingCount,
    byCoordsListings,
    currentListing,
    dispatch,
    existsOutsideOfList,
    gmapLoaded,
    isAreaUrl,
    isInitialSsrPage,
    isLoggedIn,
    isMobile,
    isNearMeUrl,
    isPadOrBuildingUrl,
    isValidAreaPage,
    isValidSearchSlug,
    page: parsedQuery.page,
    pathname,
    query: parsedQuery,
    resolveListingArea,
    showMap,
    slug,
  });

  return (
    <SplitMapTemplateClass
      currentListing={currentListing}
      dispatch={dispatch}
      fetchListings={fetchListings}
      isAreaUrl={isAreaUrl}
      isMobile={isMobile}
      isNearMeUrl={isNearMeUrl}
      isPadOrBuildingUrl={isPadOrBuildingUrl}
      isValidAreaPage={isValidAreaPage}
      isValidSearchSlug={isValidSearchSlug}
      pathname={pathname}
      previewListing={previewListing}
      setMapView={setMapView}
      showCreateSearchAlert={showCreateSearchAlert}
      showMap={showMap}
      showOptInSearchAlerts={showOptInSearchAlerts}
    />
  );
};

export { SplitMapTemplateClass };
export default SplitMapTemplate;
